"use client";

import { EdgenModalCreateAble } from "@/src/components/EdgenModal";
import { callEdgenModalWhenRootReadyAsync } from "@/src/components/EdgenModal/waitForEdgenModalRoot";
import { GiftSetGoalPaywallModalContent } from "./GiftSetGoalPaywallModalContent";
import { GIFT_SET_GOAL_PAYWALL_MODAL_PROPS } from "./giftSetGoalPaywallShell";
import type {
  GiftSetGoalPaywallResult,
  OpenGiftSetGoalPaywallModalParams,
} from "./types";

let pendingGiftSetGoalPaywallModal: Promise<GiftSetGoalPaywallResult> | null =
  null;

/**
 * 打开「Paywall – Free plan offer」弹窗（Figma `96138:32739` / `96097:351742`）。
 *
 * `await` 至用户点击关闭或主 CTA「Build my plan free」：
 * - `{ action: "close" }` — 关闭
 * - `{ action: "unlock" }` — 主 CTA
 *
 * 实现要点：
 * - 必须持有 `call()` 返回的 instance，用 `end(instance, false)` 关闭，避免 `end(false)` 误伤栈内其他弹窗
 * - `await modalInstance`，外部 end / 链式关窗时也能清理 pending，防止二次调用卡死
 */
export const openGiftSetGoalPaywallModal = (
  params: OpenGiftSetGoalPaywallModalParams = {},
): Promise<GiftSetGoalPaywallResult> => {
  if (pendingGiftSetGoalPaywallModal) {
    return pendingGiftSetGoalPaywallModal;
  }

  const { isPC = false, onComplete } = params;

  pendingGiftSetGoalPaywallModal = callEdgenModalWhenRootReadyAsync(
    async () => {
      let settled = false;
      let resolvedResult: GiftSetGoalPaywallResult = { action: "close" };
      let modalInstance: Promise<boolean> | undefined;

      const finish = (result: GiftSetGoalPaywallResult) => {
        if (settled) {
          return;
        }
        settled = true;
        resolvedResult = result;
        onComplete?.(result);
        if (modalInstance) {
          EdgenModalCreateAble.end(modalInstance, false);
        }
      };

      modalInstance = EdgenModalCreateAble.call({
        children: (
          <GiftSetGoalPaywallModalContent isPc={isPC} onFinish={finish} />
        ),
        onDismiss: () => {
          finish({ action: "close" });
        },
        ...GIFT_SET_GOAL_PAYWALL_MODAL_PROPS,
      });

      try {
        await modalInstance;
      } finally {
        if (!settled) {
          settled = true;
          resolvedResult = { action: "close" };
          onComplete?.(resolvedResult);
        }
        pendingGiftSetGoalPaywallModal = null;
      }

      return resolvedResult;
    },
  ).catch((error) => {
    pendingGiftSetGoalPaywallModal = null;
    throw error;
  });

  return pendingGiftSetGoalPaywallModal;
};
